import { POLLS_API_PREFIX } from '@/config/polls'

// ─────────────────────────────────────────────────────────────────────────────
// Types (v3.1a §3.1 Poll entity + §2.1 list row shape)
// ─────────────────────────────────────────────────────────────────────────────

export type PollType = 'single' | 'multi' | 'quiz'
export type PollStatus = 'draft' | 'scheduled' | 'live' | 'closed'
export type PollCloseReason =
  | null
  | 'manual'
  | 'auto_at_date'
  | 'message_deleted'
  | 'bot_deadmin'
  | 'owner_unsubscribed' // reserved, вариант B из §4.5 не активирован в Phase 1

// GET /api/max/polls?channelUUID= — contract B 2026-07-27
export interface PollListItem {
  id: string
  question: string
  type: PollType
  status: PollStatus
  closeReason: PollCloseReason
  publishedAt: string | null
  closedAt: string | null
  createdAt: string
  optionCount: number
  totalVoters: number
  allowRevote: boolean
  anonymous: boolean
}

export interface PollsListKpi {
  /** draft + scheduled + live + closed */
  totalPolls: number
  /** status='live' */
  activePolls: number
  /** COUNT DISTINCT maxUserID по всем polls канала */
  totalUniqueVoters: number
}

export interface PollsListResponse {
  polls: PollListItem[]
  kpi: PollsListKpi
}

export interface ChannelRightsResponse {
  granted: string[]
  missing: string[]
  required: string[]
  optional?: {
    granted: string[]
    missing: string[]
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 402 sentinel — Lab-analytics gate (v3.1a-hotfix1 §5.7.8).
// Backend возвращает 402 на /timeseries и /voters для non-Lab-owners.
// Клиент должен рендерить teaser с CTA «Подключить Lab» и deep-link из ответа.
// ─────────────────────────────────────────────────────────────────────────────

export interface LabAnalyticsRequired {
  /** literal 402 — discriminant для type-guard. */
  status: 402
  /** MAX deep-link на канал Telegraphyx Lab (магический join / профиль). */
  labChannelDeepLink: string
  /** Название канала для копии («Подпишись на *Telegraphyx Lab*»). */
  chatName: string
}

export function isLabAnalyticsRequired(x: unknown): x is LabAnalyticsRequired {
  return typeof x === 'object' && x !== null && (x as { status?: unknown }).status === 402
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-endpoint feature flags. По мере готовности B — переключаем в false.
// ─────────────────────────────────────────────────────────────────────────────

const USE_MOCKS_LIST = false      // GET /polls/?channelUUID= (B: deployed 2026-07-27)
const USE_MOCKS_CREATE = false    // POST /polls/ (B: deployed 2026-07-27)
const USE_MOCKS_PUBLISH = false   // POST /:pollId/publish (B: deployed давно, я забыл)
const USE_MOCKS_DUPLICATE = false // POST /:pollId/duplicate (B: deployed 2026-07-27)
const USE_MOCKS_CLOSE = false     // POST /:pollId/close (B: deployed 2026-07-27)
const USE_MOCKS_TIMESERIES = false // GET /:pollId/timeseries (B: deployed 2026-07-27, 402-gated)
const USE_MOCKS_VOTERS = false    // GET /:pollId/voters (B: deployed 2026-07-27, 402-gated)

// ─────────────────────────────────────────────────────────────────────────────
// Real API — GET /api/max/polls?channelUUID=<uuid>[&status=<status>]
// ─────────────────────────────────────────────────────────────────────────────

async function fetchPollsListReal(params: {
  channelUUID: string
  status?: PollStatus | 'all'
  limit?: number
  offset?: number
}): Promise<PollsListResponse | null> {
  try {
    const query = new URLSearchParams({ channelUUID: params.channelUUID })
    if (params.status && params.status !== 'all') query.set('status', params.status)
    if (params.limit != null) query.set('limit', String(params.limit))
    if (params.offset != null) query.set('offset', String(params.offset))
    // Trailing slash обязателен — backend отвечает 401 JSON только на
    // `/api/max/polls/`, без слеша nginx делает 301 → SPA fallback (HTML).
    const r = await fetch(`${POLLS_API_PREFIX}/?${query.toString()}`, {
      credentials: 'include',
    })
    if (!r.ok) return null // §7.5 #2: .catch(() => null) на любую ошибку
    return (await r.json()) as PollsListResponse
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock data — фикстура для Phase 1 разработки без бэка
// ─────────────────────────────────────────────────────────────────────────────

const NOW_STR = new Date().toISOString()
const HOURS_AGO = (h: number) => new Date(Date.now() - h * 3600 * 1000).toISOString()
const DAYS_AGO = (d: number) => new Date(Date.now() - d * 86400 * 1000).toISOString()

const MOCK_POLLS: PollListItem[] = [
  {
    id: 'mock-live-single',
    question: 'Какую тему разобрать в следующем посте?',
    type: 'single',
    status: 'live',
    closeReason: null,
    publishedAt: NOW_STR,
    closedAt: null,
    createdAt: NOW_STR,
    optionCount: 4,
    totalVoters: 412,
    allowRevote: true,
    anonymous: true,
  },
  {
    id: 'mock-live-quiz',
    question: 'Откуда родом эспрессо?',
    type: 'quiz',
    status: 'live',
    closeReason: null,
    publishedAt: HOURS_AGO(2),
    closedAt: null,
    createdAt: HOURS_AGO(2),
    optionCount: 4,
    totalVoters: 284,
    allowRevote: false,
    anonymous: true,
  },
  {
    id: 'mock-live-multi',
    question: 'Какие зёрна берёте чаще всего? (можно несколько)',
    type: 'multi',
    status: 'live',
    closeReason: null,
    publishedAt: HOURS_AGO(3),
    closedAt: null,
    createdAt: HOURS_AGO(3),
    optionCount: 6,
    totalVoters: 552,
    allowRevote: true,
    anonymous: true,
  },
  {
    id: 'mock-scheduled',
    question: 'Pulse: как вам неделя в канале?',
    type: 'single',
    status: 'scheduled',
    closeReason: null,
    publishedAt: null,
    closedAt: null,
    createdAt: HOURS_AGO(1),
    optionCount: 5,
    totalVoters: 0,
    allowRevote: false,
    anonymous: true,
  },
  {
    id: 'mock-closed-quiz',
    question: 'Сколько грамм зерна на двойной эспрессо?',
    type: 'quiz',
    status: 'closed',
    closeReason: 'auto_at_date',
    publishedAt: DAYS_AGO(5),
    closedAt: DAYS_AGO(3),
    createdAt: DAYS_AGO(5),
    optionCount: 4,
    totalVoters: 704,
    allowRevote: false,
    anonymous: true,
  },
  {
    id: 'mock-orphaned',
    question: 'Тестовый опрос (был удалён из канала)',
    type: 'single',
    status: 'closed',
    closeReason: 'message_deleted',
    publishedAt: DAYS_AGO(4),
    closedAt: DAYS_AGO(4),
    createdAt: DAYS_AGO(4),
    optionCount: 3,
    totalVoters: 12,
    allowRevote: false,
    anonymous: true,
  },
  {
    id: 'mock-draft',
    question: 'Помогите выбрать обложку для нового гайда',
    type: 'single',
    status: 'draft',
    closeReason: null,
    publishedAt: null,
    closedAt: null,
    createdAt: HOURS_AGO(20),
    optionCount: 3,
    totalVoters: 0,
    allowRevote: true,
    anonymous: true,
  },
]

const MOCK_KPI: PollsListKpi = {
  totalPolls: MOCK_POLLS.length,
  activePolls: MOCK_POLLS.filter((p) => p.status === 'live').length,
  totalUniqueVoters: MOCK_POLLS.reduce((s, p) => s + p.totalVoters, 0),
}

function fetchPollsListMock(params: {
  status?: PollStatus | 'all'
}): PollsListResponse {
  const filtered =
    !params.status || params.status === 'all'
      ? MOCK_POLLS
      : MOCK_POLLS.filter((p) => p.status === params.status)
  return { polls: filtered, kpi: MOCK_KPI }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — единая точка входа. Feature flag USE_MOCKS переключает мок/бэк.
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchPollsList(params: {
  channelUUID: string
  status?: PollStatus | 'all'
  /** Default 50, max 200 (§3.2 rate-limit + response weight). */
  limit?: number
  offset?: number
}): Promise<PollsListResponse | null> {
  if (USE_MOCKS_LIST) {
    // Симулируем небольшой RTT для реалистичности UI
    await new Promise((r) => setTimeout(r, 120))
    return fetchPollsListMock({ status: params.status })
  }
  return fetchPollsListReal(params)
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE / PUBLISH — v3.1a §2.2, §4.1-§4.2, §5.7.3 (idempotency + CAS)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST / body — B contract 2026-07-27. `settings` и `schedule` — nested.
 * `letter` в options опциональный (auto А/Б/В если пусто, 1..4 chars).
 * Constraints: 2..10 options; quiz → ровно 1 isCorrect=true;
 * multi → maxOptionsPerUser ∈ [1, options.length]; single/quiz → maxOptionsPerUser=null.
 */
export interface CreatePollPayload {
  channelUUID: string
  type: PollType
  /** 1..300 chars */
  question: string
  /** 0..500 chars, optional. Не отправляем ключ если пусто. */
  description?: string
  options: { text: string; letter?: string; isCorrect?: boolean }[]
  settings: {
    anonymous: boolean
    showResultsBeforeClose: boolean
    allowRevote: boolean
    /** Phase 1: всегда false (в UI disabled). */
    pinToChannel: boolean
    /** Только type='multi'; null для single/quiz. */
    maxOptionsPerUser?: number | null
  }
  schedule: {
    publishMode: 'immediate' | 'scheduled' | 'pulse'
    /** Required если scheduled/pulse. */
    publishAt?: string
    pulseIntervalDays?: number
    closeMode: 'in_1h' | 'in_1d' | 'in_1w' | 'at_date' | 'manual'
    /** Required если closeMode='at_date'. */
    closeAt?: string
  }
  /** ULID клиента для idempotency-retry на 23505 (§4.4.4). */
  createAttemptId?: string
}

export interface CreatePollResponse {
  pollId: string
  status: PollStatus
  createdAt: string
}

export interface DuplicatePollResponse {
  pollId: string
  status: 'draft'
  createdAt: string
}

export interface PublishPollResponse {
  pollId: string
  status: PollStatus
  maxMessageID?: string
  /** 409 conflict body: уже опубликован (§4.2.5). */
  conflict?: {
    existingMessageID?: string
    orphanCleanupPending?: boolean
  }
}

export type BotMissingRightsError = {
  code: 'BOT_MISSING_RIGHTS'
  missing: string[]
  status: number
}

/** POST /:pollId/close — new shape (B 2026-07-27). */
export interface ClosePollResponse {
  pollId: string
  closedAt: string
}

/** Discriminated error for close-action UI feedback. */
export type ClosePollError =
  | { code: 'FORBIDDEN'; status: 403 }        // юзер не автор
  | { code: 'NOT_FOUND'; status: 404 }        // poll удалён / чужой канал
  | { code: 'ALREADY_CLOSED'; status: 409 }   // race: закрыт другой tab'ой / auto_at_date
  | { code: 'NETWORK'; status: 0 }            // сеть/timeout/JSON

/**
 * PATCH /:pollId body — B contract 2026-07-28.
 * type и channelUUID immutable (фиксируются при POST /). Всё остальное можно
 * редактировать. options — полная замена (order по индексу). settings/schedule
 * — целиком (partial-inside НЕ поддерживается — шлём весь объект с дефолтами).
 */
export type UpdatePollPayload = Omit<CreatePollPayload, 'channelUUID' | 'type' | 'createAttemptId'>

export interface UpdatePollResponse {
  pollId: string
  updatedAt: string
}

/**
 * Ошибка при PATCH-редактировании draft'а. `NOT_DRAFT` — race: пока юзер
 * редактировал, другая вкладка / auto — опубликовала. F показывает toast и
 * редиректит на `/polls/<id>` (results-view).
 */
export type UpdatePollError =
  | { code: 'FORBIDDEN'; status: 403 }
  | { code: 'NOT_FOUND'; status: 404 }
  | { code: 'NOT_DRAFT'; status: 409 }
  | { code: 'VALIDATION'; status: 400; error?: string }
  | { code: 'NETWORK'; status: 0 }


async function createPollReal(payload: CreatePollPayload): Promise<CreatePollResponse | null> {
  try {
    // Trailing slash обязателен — иначе nginx 301 GET /api/max/polls/ и body POST теряется.
    const r = await fetch(`${POLLS_API_PREFIX}/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!r.ok) return null
    return (await r.json()) as CreatePollResponse
  } catch {
    return null
  }
}

export async function checkChannelBotRights(channelUUID: string): Promise<ChannelRightsResponse | null> {
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/channel-rights?channelUUID=${encodeURIComponent(channelUUID)}`, {
      credentials: 'include',
    })
    if (!r.ok) return null
    return (await r.json()) as ChannelRightsResponse
  } catch {
    return null
  }
}

async function publishPollReal(pollId: string): Promise<PublishPollResponse | BotMissingRightsError | null> {
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}/publish`, {
      method: 'POST',
      credentials: 'include',
    })
    if (r.status === 409) {
      // §4.2.5 CAS conflict: уже опубликовано параллельно
      const body = (await r.json().catch(() => ({}))) as PublishPollResponse['conflict']
      return { pollId, status: 'live', conflict: body ?? {} }
    }
    if (!r.ok) {
      const body = (await r.json().catch(() => ({}))) as { code?: string; missing?: string[] }
      if (body.code === 'BOT_MISSING_RIGHTS') {
        return {
          code: 'BOT_MISSING_RIGHTS',
          missing: Array.isArray(body.missing) ? body.missing : [],
          status: r.status,
        }
      }
      return null
    }
    return (await r.json()) as PublishPollResponse
  } catch {
    return null
  }
}

// Mock: генерируем pollId вида mock-<random> и симулируем 200 sunny-day.
function createPollMock(payload: CreatePollPayload): CreatePollResponse {
  const pollId = 'mock-created-' + Math.random().toString(36).slice(2, 10)
  // eslint-disable-next-line no-console
  console.info('[polls mock] createPoll', pollId, payload)
  return { pollId, status: 'draft', createdAt: new Date().toISOString() }
}

function publishPollMock(pollId: string): PublishPollResponse {
  return {
    pollId,
    status: 'live',
    maxMessageID: 'mock-msg-' + Math.random().toString(36).slice(2, 8),
  }
}

export async function createPoll(payload: CreatePollPayload): Promise<CreatePollResponse | null> {
  if (USE_MOCKS_CREATE) {
    await new Promise((r) => setTimeout(r, 220))
    return createPollMock(payload)
  }
  return createPollReal(payload)
}

export async function publishPoll(pollId: string): Promise<PublishPollResponse | BotMissingRightsError | null> {
  if (USE_MOCKS_PUBLISH) {
    await new Promise((r) => setTimeout(r, 400))
    return publishPollMock(pollId)
  }
  return publishPollReal(pollId)
}

/**
 * PATCH /:pollId — обновляет draft-опрос (B contract 2026-07-28).
 * Возвращает `UpdatePollResponse` при 200, `UpdatePollError` union при 400/403/404/409.
 */
export async function updatePoll(
  pollId: string,
  payload: UpdatePollPayload,
): Promise<UpdatePollResponse | UpdatePollError> {
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (r.status === 403) return { code: 'FORBIDDEN', status: 403 }
    if (r.status === 404) return { code: 'NOT_FOUND', status: 404 }
    if (r.status === 409) return { code: 'NOT_DRAFT', status: 409 }
    if (r.status === 400) {
      const body = (await r.json().catch(() => ({}))) as { error?: string }
      return { code: 'VALIDATION', status: 400, error: body.error }
    }
    if (!r.ok) return { code: 'NETWORK', status: 0 }
    return (await r.json()) as UpdatePollResponse
  } catch {
    return { code: 'NETWORK', status: 0 }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// POLL DETAIL / RESULTS / TIMESERIES / VOTERS (v3.1a §4.7/§4.8 + §4.2.2)
// Contract от B 2026-07-24: `GET /api/max/polls/:pollId` — nested shape.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Опция опроса — без `count`/`pct` (эти живут в `totals.byOption`).
 * `isCorrect` для quiz-опросов **до голосования** приходит `false` у всех
 * вариантов (server hides). После голосования / после close правильный
 * ответ известен через отдельное поле `correctOptionIds` в ответе detail.
 */
export interface PollOption {
  id: string
  order: number
  letter: string
  text: string
  isCorrect: boolean
}

export interface PollMyVote {
  /** Массив ВСЕГДА: для single/quiz — [1 элемент], для multi — [1..N]. */
  optionIds: string[]
  votedAt: string
  /** true если у голосующего есть superseded голоса (менял мнение). */
  revoted: boolean
}

export interface PollByOption {
  optionId: string
  count: number
  pct: number
}

export interface PollTotals {
  /** DISTINCT maxUserID — уникальные голосующие (для multi может отличаться от sum(count)). */
  totalVoters: number
  byOption: PollByOption[]
}

/** Nested-shape ответа `GET /:pollId` от B. */
export interface PollDetail {
  poll: {
    id: string
    channelUUID: string
    /** bigint→string на сервере — приводить не надо. */
    createdBy: string
    question: string
    /** Опциональное описание (addendum 2026-07-24). null/undefined если автор не заполнил. */
    description?: string | null
    type: PollType
    status: PollStatus
    closeReason: PollCloseReason
    closeAt: string | null
    closedAt: string | null
    anonymous: boolean
    allowRevote: boolean
    /** ISO8601, используется в ETag `W/"<updatedAt_ms>-<totalVotes>"`. */
    updatedAt: string
  }
  options: PollOption[]
  /** null если юзер не голосовал (или для админа который не голосовал). */
  myVote: PollMyVote | null
  /**
   * null если !showResultsBeforeClose && !isAdmin && myVote===null —
   * т.е. юзер ещё не голосовал, автор не он, результаты пока скрыты.
   * Всегда null → показываем placeholder «Голосуйте, чтобы увидеть результат».
   */
  totals: PollTotals | null
  /**
   * ТОЛЬКО type='quiz' AND (myVote!==null OR status='closed').
   * Массив ID правильных ответов (v3.1a — 1 элемент, но future-proof).
   */
  correctOptionIds?: string[]
  /** BACKEND-вывод: `poll.createdBy === maxUserID`. Читать как есть. */
  isAdmin: boolean
  /**
   * BACKEND-вывод (addendum 2026-07-24): `voter.maxUserID === max_channel.maxAdminID`
   * для `poll.channelUUID`. Отличается от `isAdmin` (тот про автора опроса).
   * Виктор использует в правиле `showFooter` в миниаппе (виралку не показываем
   * админу канала-хоста). В ЛК на странице результатов автор обычно совпадает
   * с owner канала, поле присутствует для полноты shape'а — не используется UI-ом.
   */
  isChannelAdmin?: boolean
  /** Weak ETag `W/"<updatedAt_ms>-<totalVotes>"` — можно брать из body или из response header. */
  etag: string
}

/**
 * B contract (2026-07-27): timeseries отдаёт ОДНУ общую линию
 * `count` per bucket (не per-option). Frontend строит один график
 * «всего голосов во времени». Итоговое число дублируется в `totalVoters`
 * (для label «уникальных X»).
 */
export interface PollTimeseriesPoint {
  ts: string
  count: number
}

export interface PollTimeseries {
  bucket: 'minute' | 'hour' | 'day'
  points: PollTimeseriesPoint[]
  totalVoters: number
}

/**
 * B contract `GET /:pollId/voters` (2026-07-27).
 * `optionIds` — массив (multi-опрос отдаёт N выбранных). Для single/quiz — [1].
 * `maxUserID` — bigint→string; `null` не бывает (аноним = скрывается тем что endpoint под Lab-gate).
 * `displayName` — из MAX; может быть `null` для юзеров без публичного имени.
 */
export interface PollVoter {
  maxUserID: string
  votedAt: string
  optionIds: string[]
  wasSubscribedAtVote: boolean | null
  displayName: string | null
}

/** GET /:pollId/voters ?tab= — cursor pagination (B 2026-07-27). */
export type VotersTab = 'all' | 'subscribed_before' | 'subscribed_after'

export interface VotersResponse {
  items: PollVoter[]
  /** null означает «страница последняя». Передавать в след. запрос ?cursor=<opaque>. */
  nextCursor: string | null
  hasMore: boolean
}

// Real fetch с If-None-Match ETag (§4.2.2 polling)
// ETag предпочитаем из response header; если нет — берём из body (B шлёт оба).
async function fetchPollDetailReal(pollId: string, etag?: string): Promise<{ data: PollDetail | null; etag: string | null; notModified: boolean }> {
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}`, {
      credentials: 'include',
      headers: etag ? { 'If-None-Match': etag } : undefined,
    })
    if (r.status === 304) return { data: null, etag: etag ?? null, notModified: true }
    if (!r.ok) return { data: null, etag: null, notModified: false }
    const body = (await r.json()) as PollDetail
    const nextEtag = r.headers.get('ETag') || body.etag || null
    return { data: body, etag: nextEtag, notModified: false }
  } catch {
    return { data: null, etag: null, notModified: false }
  }
}

// Mock — обновлён под nested shape B (тот же контракт что prod)
function fetchPollDetailMock(pollId: string): PollDetail {
  const listItem = MOCK_POLLS.find((p) => p.id === pollId)
  const type: PollType = listItem?.type ?? 'single'
  const status: PollStatus = listItem?.status ?? 'live'
  const options: PollOption[] = [
    { id: `${pollId}-opt-0`, order: 0, letter: 'А', text: 'Эспрессо для дома: как настроить кофемашину', isCorrect: false },
    { id: `${pollId}-opt-1`, order: 1, letter: 'Б', text: 'Зёрна: arabica vs robusta, обжарка', isCorrect: type === 'quiz' },
    { id: `${pollId}-opt-2`, order: 2, letter: 'В', text: 'Альтернативные способы: V60, аэропресс, кемекс', isCorrect: false },
    { id: `${pollId}-opt-3`, order: 3, letter: 'Г', text: 'Кофейни Москвы и Питера: куда сходить', isCorrect: false },
  ]
  const counts = [108, 157, 91, 56]
  const totalVoters = counts.reduce((s, c) => s + c, 0)
  const totals: PollTotals = {
    totalVoters,
    byOption: options.map((o, i) => ({
      optionId: o.id,
      count: counts[i],
      pct: Math.round((counts[i] / totalVoters) * 100),
    })),
  }
  return {
    poll: {
      id: pollId,
      channelUUID: 'mock-channel',
      createdBy: '8931841',
      question: listItem?.question ?? 'Какую тему разобрать в следующем посте?',
      type,
      status,
      closeReason: listItem?.closeReason ?? null,
      closeAt: new Date(Date.now() + 20 * 3600 * 1000).toISOString(),
      closedAt: status === 'closed' ? new Date(Date.now() - 3 * 86400 * 1000).toISOString() : null,
      anonymous: listItem?.anonymous ?? true,
      allowRevote: listItem?.allowRevote ?? true,
      updatedAt: new Date().toISOString(),
    },
    options,
    myVote: null,
    totals,
    correctOptionIds: type === 'quiz' ? [options[1].id] : undefined,
    isAdmin: true,
    etag: `W/"${Date.now()}-${totalVoters}"`,
  }
}

// Отдельный флаг: shape /:pollId известен, B задеплоил 2026-07-24. list/create
// пока моки — B их доделывает P7/A6+A7 (shape уточним, потом переключим).
const USE_MOCKS_DETAIL = false

export async function fetchPollDetail(
  pollId: string,
  etag?: string,
): Promise<{ data: PollDetail | null; etag: string | null; notModified: boolean }> {
  if (USE_MOCKS_DETAIL) {
    await new Promise((r) => setTimeout(r, 150))
    return { data: fetchPollDetailMock(pollId), etag: 'mock-etag-' + Date.now(), notModified: false }
  }
  return fetchPollDetailReal(pollId, etag)
}

// Timeseries — mock: 8 buckets, каждый час растёт общий count
function fetchPollTimeseriesMock(pollId: string): PollTimeseries {
  const detail = fetchPollDetailMock(pollId)
  const totalVoters = detail.totals?.totalVoters ?? 0
  const now = Date.now()
  const points: PollTimeseriesPoint[] = []
  for (let h = 7; h >= 0; h--) {
    const ts = new Date(now - h * 3600 * 1000).toISOString()
    const progress = (8 - h) / 8
    const count = Math.round(totalVoters * progress * (0.85 + Math.random() * 0.3))
    points.push({ ts, count })
  }
  return { bucket: 'hour', points, totalVoters }
}

/**
 * GET /:pollId/timeseries — 402 gate для non-Lab автора канала.
 * Возвращает `LabAnalyticsRequired` (status:402) вместо null, чтобы UI мог
 * отрендерить teaser с CTA «Подключить Lab» и deep-link. `null` = сетевая/other ошибка.
 */
export async function fetchPollTimeseries(
  pollId: string,
): Promise<PollTimeseries | LabAnalyticsRequired | null> {
  if (USE_MOCKS_TIMESERIES) {
    await new Promise((r) => setTimeout(r, 200))
    return fetchPollTimeseriesMock(pollId)
  }
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}/timeseries?bucket=hour`, {
      credentials: 'include',
    })
    if (r.status === 402) {
      const body = (await r.json().catch(() => ({}))) as Partial<LabAnalyticsRequired>
      return {
        status: 402,
        labChannelDeepLink: body.labChannelDeepLink ?? '',
        chatName: body.chatName ?? 'Telegraphyx Lab',
      }
    }
    if (!r.ok) return null
    return (await r.json()) as PollTimeseries
  } catch {
    return null
  }
}

// Voters mock — cursor pagination (одна страница, hasMore=false), поля под B contract 27.07
function fetchPollVotersMock(pollId: string, tab: VotersTab = 'all'): VotersResponse {
  const detail = fetchPollDetailMock(pollId)
  const all: PollVoter[] = Array.from({ length: 8 }).map((_, i) => ({
    maxUserID: `mock-voter-${i}`,
    votedAt: new Date(Date.now() - i * 8 * 60 * 1000).toISOString(),
    optionIds: [detail.options[i % detail.options.length].id],
    wasSubscribedAtVote: i < 6,
    displayName: null,
  }))
  const items =
    tab === 'subscribed_before'
      ? all.filter((v) => v.wasSubscribedAtVote === true)
      : tab === 'subscribed_after'
      ? all.filter((v) => v.wasSubscribedAtVote === false)
      : all
  return { items, nextCursor: null, hasMore: false }
}

/**
 * GET /:pollId/voters — cursor pagination + tab фильтр + 402 gate.
 * @param cursor — opaque токен с прошлой страницы. Не передавать на первый запрос.
 */
export async function fetchPollVoters(params: {
  pollId: string
  tab?: VotersTab
  cursor?: string
  limit?: number
}): Promise<VotersResponse | LabAnalyticsRequired | null> {
  const { pollId, tab = 'all', cursor, limit } = params
  if (USE_MOCKS_VOTERS) {
    await new Promise((r) => setTimeout(r, 180))
    return fetchPollVotersMock(pollId, tab)
  }
  try {
    const qs = new URLSearchParams({ tab })
    if (cursor) qs.set('cursor', cursor)
    if (limit) qs.set('limit', String(limit))
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}/voters?${qs.toString()}`, {
      credentials: 'include',
    })
    if (r.status === 402) {
      const body = (await r.json().catch(() => ({}))) as Partial<LabAnalyticsRequired>
      return {
        status: 402,
        labChannelDeepLink: body.labChannelDeepLink ?? '',
        chatName: body.chatName ?? 'Telegraphyx Lab',
      }
    }
    if (!r.ok) return null
    return (await r.json()) as VotersResponse
  } catch {
    return null
  }
}

// Actions — close early / duplicate.
// closePoll: 200 → ClosePollResponse; 403/404/409 → ClosePollError (typed).
// Union return, UI различает `code`.
export async function closePoll(
  pollId: string,
): Promise<ClosePollResponse | ClosePollError> {
  if (USE_MOCKS_CLOSE) {
    await new Promise((r) => setTimeout(r, 250))
    return { pollId, closedAt: new Date().toISOString() }
  }
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}/close`, {
      method: 'POST',
      credentials: 'include',
    })
    if (r.status === 403) return { code: 'FORBIDDEN', status: 403 }
    if (r.status === 404) return { code: 'NOT_FOUND', status: 404 }
    if (r.status === 409) return { code: 'ALREADY_CLOSED', status: 409 }
    if (!r.ok) return { code: 'NETWORK', status: 0 }
    return (await r.json()) as ClosePollResponse
  } catch {
    return { code: 'NETWORK', status: 0 }
  }
}

export async function duplicatePoll(pollId: string): Promise<DuplicatePollResponse | null> {
  if (USE_MOCKS_DUPLICATE) {
    await new Promise((r) => setTimeout(r, 200))
    return {
      pollId: 'mock-dup-' + Math.random().toString(36).slice(2, 8),
      status: 'draft',
      createdAt: new Date().toISOString(),
    }
  }
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/${encodeURIComponent(pollId)}/duplicate`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!r.ok) return null
    return (await r.json()) as DuplicatePollResponse
  } catch {
    return null
  }
}
